/**
 * Venster over de pagina heen met een video erin.
 *
 * Eén venster voor de hele pagina, aangesproken via een link met [data-episode-video].
 * De href is de bron, de waarde van het attribuut zegt of het een iframe (YouTube,
 * Vimeo) of een <video> moet worden -- dat is in PHP al uitgezocht.
 *
 * Het openen is een GSAP-tijdlijn: eerst het waas erachter, dan het venster zelf dat
 * van onderaf omhoog schuift en op maat komt. Sluiten draait dezelfde tijdlijn terug,
 * iets sneller, en gooit daarna de iframe weg -- anders speelt het geluid door.
 */

import gsap from 'gsap';

import { lockScroll, unlockScroll } from './scroll-lock';

const OPEN = 0.55;

let modal = null;
let timeline = null;
let lastTrigger = null;

export default function initVideoModal() {
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-episode-video]');

    if (! trigger) {
      return;
    }

    event.preventDefault();
    open(trigger);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && modal && modal.classList.contains('is-open')) {
      close();
    }
  });
}

function open(trigger) {
  const src = trigger.getAttribute('href');

  if (! src) {
    return;
  }

  lastTrigger = trigger;

  const element = build();
  const frame = element.querySelector('[data-video-modal-frame]');
  const label = trigger.getAttribute('data-episode-title') || 'Video';

  element.setAttribute('aria-label', label);
  frame.innerHTML = '';
  frame.appendChild(media(trigger.getAttribute('data-episode-video'), src, label));

  element.classList.add('is-open');
  lockScroll();

  // Elke keer een verse tijdlijn: een halverwege teruggedraaide vorige zou het venster
  // op de tussenstand laten beginnen.
  timeline = gsap.timeline({ defaults: { ease: 'power3.out' } })
    .fromTo(element.querySelector('.video-modal__backdrop'), { opacity: 0 }, { opacity: 1, duration: OPEN * 0.6 })
    .fromTo(
      element.querySelector('.video-modal__dialog'),
      { opacity: 0, y: 48, scale: 0.94 },
      { opacity: 1, y: 0, scale: 1, duration: OPEN },
      `-=${OPEN * 0.4}`
    );

  element.querySelector('.video-modal__close').focus({ preventScroll: true });
}

function close() {
  if (! modal || ! timeline) {
    return;
  }

  const element = modal;

  timeline.timeScale(1.5).reverse().eventCallback('onReverseComplete', () => {
    element.classList.remove('is-open');
    // Pas nu leeghalen: een iframe die tijdens de animatie verdwijnt laat een gat achter,
    // en een die blijft staan speelt onzichtbaar door.
    element.querySelector('[data-video-modal-frame]').innerHTML = '';
    unlockScroll();

    if (lastTrigger && document.contains(lastTrigger)) {
      lastTrigger.focus({ preventScroll: true });
    }

    lastTrigger = null;
  });
}

/**
 * Het venster zelf, één keer gemaakt en daarna hergebruikt.
 */
function build() {
  if (modal) {
    return modal;
  }

  modal = document.createElement('div');
  modal.className = 'video-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.innerHTML = `
    <button type="button" class="video-modal__backdrop" tabindex="-1" aria-label="Sluiten"></button>
    <div class="video-modal__dialog">
      <button type="button" class="video-modal__close" aria-label="Sluiten">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>
      </button>
      <div class="video-modal__frame" data-video-modal-frame></div>
    </div>
  `;

  modal.querySelector('.video-modal__backdrop').addEventListener('click', close);
  modal.querySelector('.video-modal__close').addEventListener('click', close);

  // De focus binnen het venster houden zolang het openstaat.
  modal.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') {
      return;
    }

    const focusable = modal.querySelectorAll('.video-modal__close, iframe, video');

    if (! focusable.length) {
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (! event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.body.appendChild(modal);

  return modal;
}

function media(type, src, label) {
  if (type === 'file') {
    const video = document.createElement('video');

    video.src = src;
    video.controls = true;
    video.autoplay = true;
    video.setAttribute('controlsList', 'nodownload');
    video.setAttribute('playsinline', '');

    return video;
  }

  const iframe = document.createElement('iframe');

  iframe.src = src;
  iframe.title = label;
  iframe.allow = 'accelerometer; autoplay; encrypted-media; picture-in-picture; fullscreen';
  iframe.setAttribute('allowfullscreen', '');

  return iframe;
}


